/**
 * Design-template switcher (owner ask 2026-07-22 Task 4 — "user can pick 'our style' (current) or
 * 'Pro Tools'"). `data-theme` on `<html>` is the ONE seam every themed CSS rule keys off (see
 * style.css's `[data-theme="protools"]` block); this module owns reading/writing/persisting the
 * live choice. It never touches Babylon or the 3D canvas (CLAUDE.md's "scene.ts is the only file
 * allowed to import Babylon" — there is no theming seam into the canvas yet, so both themes render
 * the same 3D viewport; see style.css's own note on that).
 *
 * The attribute is applied EARLIER than this module even loads — `public/theme-boot.js`, loaded by a
 * blocking `<head>` script in `index.html` (a file, not inline, since 01.11.2026: CSP), reads the
 * SAME `localStorage` key synchronously, before any stylesheet/module runs, so there is never a
 * flash of the wrong theme on load. This module is what live UI (the side-nav switcher) calls AFTER
 * boot to change the theme, and what anything else can call to read the CURRENT theme back out.
 */

/** MUST match the literal key in `public/theme-boot.js` — that script can't import this module (it
 *  has to run before the module graph exists), so the two copies are kept in sync by
 *  `src/csp-no-inline-script.test.ts`, not by shared code. */
const STORAGE_KEY = "scb-theme";

export type ThemeName = "scb" | "protools";

function isThemeName(value: unknown): value is ThemeName {
  return value === "scb" || value === "protools";
}

/** The theme actually applied right now, read off the live DOM attribute — the attribute IS the
 *  source of truth (no separate in-memory copy that could drift from what's really rendered). */
export function getTheme(): ThemeName {
  const attr = document.documentElement.getAttribute("data-theme");
  return isThemeName(attr) ? attr : "scb";
}

/** Applies the theme immediately (sets the attribute every themed CSS rule matches on) and
 *  persists it for next load. Persistence is best-effort: a private-mode/quota failure still
 *  leaves the attribute applied for the rest of THIS session, it just won't survive a reload. */
export function setTheme(theme: ThemeName): void {
  document.documentElement.setAttribute("data-theme", theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* localStorage unavailable — theme still applies for this session. */
  }
}
